import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Columns3, LineChart, MessageCircle, ChevronDown, ArrowRight, UserRound, CheckCheck, Check, ShieldCheck, Star, Sparkles } from "lucide-react";
import { PAYWALL_DATE, FREE_UNTIL_LABEL } from "@/config/pricing";
import { startGoogleLogin } from "@/lib/login";
import usePageMeta from "@/lib/usePageMeta";

/* ── Sparkle Countdown Banner (home page only) ──────────────────────
   Live countdown to the early-access deadline (PAYWALL_DATE).
   Premium gradient, sweeping shine, twinkling sparkle icon. */
const SparkleChip = ({ value, label }) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div
      className="sparkle-chip rounded-md px-2.5 py-1 flex flex-col items-center min-w-[44px] leading-none"
      data-testid={`sparkle-countdown-${label.toLowerCase()}`}
    >
      <span className="sparkle-chip-num text-[15px] sm:text-base font-semibold tracking-tight">
        {pad(value)}
      </span>
      <span className="text-[9px] uppercase tracking-[0.14em] text-white/55 mt-0.5">
        {label}
      </span>
    </div>
  );
};

function SparkleBanner() {
  const nav = useNavigate();
  const [t, setT] = useState(() => Math.max(0, PAYWALL_DATE - new Date()));

  useEffect(() => {
    const id = setInterval(() => {
      setT(Math.max(0, PAYWALL_DATE - new Date()));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (t <= 0) return null; // banner disappears once the offer ends

  const days = Math.floor(t / 86_400_000);
  const hours = Math.floor((t % 86_400_000) / 3_600_000);
  const mins = Math.floor((t % 3_600_000) / 60_000);
  const secs = Math.floor((t % 60_000) / 1000);

  return (
    <div
      className="sparkle-banner no-print w-full cursor-pointer"
      onClick={() => nav("/pricing")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && nav("/pricing")}
      data-testid="sparkle-countdown-banner"
      aria-label="Early access ends soon — view pricing"
    >
      <div className="relative z-[1] max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center justify-center sm:justify-between gap-3 flex-wrap">
        {/* Left: sparkle icon + headline */}
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="sparkle-icon-wrap inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-amber-300/90 to-amber-500/90 shrink-0">
            <Sparkles className="w-4 h-4 text-[#1E1B4B]" strokeWidth={2.5} />
          </span>
          <span className="sparkle-pill hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200">
            Limited
          </span>
          <span className="text-[13px] sm:text-sm font-medium text-white/95 truncate">
            Early access ends in
          </span>
        </div>

        {/* Center: live countdown */}
        <div className="flex items-center gap-1.5 sm:gap-2" aria-live="polite">
          <SparkleChip value={days} label="Days" />
          <span className="text-white/40 font-semibold text-sm">:</span>
          <SparkleChip value={hours} label="Hrs" />
          <span className="text-white/40 font-semibold text-sm">:</span>
          <SparkleChip value={mins} label="Min" />
          <span className="text-white/40 font-semibold text-sm">:</span>
          <SparkleChip value={secs} label="Sec" />
        </div>

        {/* Right: CTA */}
        <div className="hidden md:flex items-center gap-1.5 text-[13px] font-medium text-white/90 hover:text-white transition">
          <span>Free until {FREE_UNTIL_LABEL} · ₹0 today</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </div>
  );
}


const PRINCIPLES = [
  {
    head: "Decision first · Transparency above all",
    body: [
      ["Decision first", "Optimise for choices, not information density."],
      ["Transparency above all", "Every calculation shows its assumptions."],
    ],
  },
  {
    head: "Trust the product · Data before opinion",
    body: [
      ["Trust the product", "Recommendations must be explainable in plain English."],
      ["Data before opinion", "If data is unavailable, we say so. No fabrications."],
    ],
  },
  {
    head: "Explain before comparing · Trade-offs always",
    body: [
      ["Explain before comparing", "Rank 12 means little without context. We provide it."],
      ["Trade-offs always", "Benefits, risks, costs, career impact. Every time."],
    ],
  },
  {
    head: "Coach, don't decide · Simplicity wins",
    body: [
      ["Coach, don't decide", "We refuse to say 'choose this'. We help you weigh."],
      ["Simplicity wins", "Complex decisions deserve calm interfaces."],
    ],
  },
];

/* Hero — rotating Unimora AI-recommendation (Best-Fit) data — auto-swaps every 4s.
   Each set is a realistic profile-fit story for one university. */
const BEST_FIT_SETS = [
  {
    name: "University of Melbourne",
    tagline: "Your strongest match",
    score: 92,
    why: [
      ["Highest career ROI", "4.2× vs 3.4× next best."],
      ["Strong admission fit", "above IB median."],
      ["Best value", "₹20 L cheaper all-in."],
    ],
    stats: [
      { k: "Total cost", v: "₹1.32 Cr" },
      { k: "QS rank", v: "#13" },
      { k: "Employment", v: "96%" },
      { k: "Post-study work", v: "4 yrs" },
    ],
  },
  {
    name: "University of Oxford",
    tagline: "Reach with strong upside",
    score: 89,
    why: [
      ["Top-tier brand value", "exceptional global mobility."],
      ["Within IB headroom", "median admit 39/45."],
      ["High-ROI tracks", "4.6× vs 3.8× next best."],
    ],
    stats: [
      { k: "Total cost", v: "₹1.56 Cr" },
      { k: "QS rank", v: "#3" },
      { k: "Employment", v: "94%" },
      { k: "Post-study work", v: "2 yrs" },
    ],
  },
  {
    name: "NUS Singapore",
    tagline: "Closest fit on budget",
    score: 90,
    why: [
      ["Strong career outcomes", "95% employed within 6 months."],
      ["Lower total cost", "₹24 L cheaper than next pick."],
      ["Family-friendly distance", "5-hr flight to home."],
    ],
    stats: [
      { k: "Total cost", v: "₹1.08 Cr" },
      { k: "QS rank", v: "#8" },
      { k: "Employment", v: "95%" },
      { k: "Post-study work", v: "3 yrs" },
    ],
  },
  {
    name: "TU Munich",
    tagline: "Best value, world-class engineering",
    score: 87,
    why: [
      ["Near-zero tuition", "₹93 L cheaper all-in."],
      ["Engineering powerhouse", "ranked top-30 globally."],
      ["EU career runway", "18-mo job-seeker visa."],
    ],
    stats: [
      { k: "Total cost", v: "₹31 L" },
      { k: "QS rank", v: "#28" },
      { k: "Employment", v: "93%" },
      { k: "Post-study work", v: "18 mo" },
    ],
  },
];

/* Hero — rotating compare-table data (auto-swaps every 3s)
   "Total cost" = realistic tuition + living for the program's standard duration
   (AU/UK = 3 yrs, US/CA/SG = 4 yrs, DE/CH = 3 yrs UG). bestIdx marks the
   cheapest / best-ranked / highest-employment value in each row. */
const COMPARE_SETS = [
  {
    cols: [
      { name: "Melbourne", color: "#2DA877" },
      { name: "Toronto",   color: "#3B7DE0" },
      { name: "TU Munich", color: "#7A5AF0" },
    ],
    rows: [
      // Tuition + Living, full program
      { label: "Total cost (INR)", vals: ["₹1.32 Cr", "₹1.52 Cr", "₹31 L"],  bestIdx: 2 },
      { label: "QS rank",          vals: ["#13",      "#21",      "#28"],    bestIdx: 0 },
      { label: "Employment",       vals: ["96%",      "88%",      "93%"],    bestIdx: 0 },
    ],
    stars: [5, 3, 4],
  },
  {
    cols: [
      { name: "Oxford",   color: "#2DA877" },
      { name: "UCL",      color: "#3B7DE0" },
      { name: "Imperial", color: "#7A5AF0" },
    ],
    rows: [
      { label: "Total cost (INR)", vals: ["₹1.56 Cr", "₹1.35 Cr", "₹1.47 Cr"], bestIdx: 1 },
      { label: "QS rank",          vals: ["#3",       "#9",       "#6"],       bestIdx: 0 },
      { label: "Employment",       vals: ["94%",      "89%",      "92%"],      bestIdx: 0 },
    ],
    stars: [5, 4, 4],
  },
  {
    cols: [
      { name: "NUS",      color: "#2DA877" },
      { name: "ETH",      color: "#3B7DE0" },
      { name: "Stanford", color: "#7A5AF0" },
    ],
    rows: [
      { label: "Total cost (INR)", vals: ["₹1.08 Cr", "₹63 L",  "₹3.04 Cr"], bestIdx: 1 },
      { label: "QS rank",          vals: ["#8",       "#7",     "#6"],       bestIdx: 2 },
      { label: "Employment",       vals: ["95%",      "97%",    "98%"],      bestIdx: 2 },
    ],
    stars: [4, 5, 5],
  },
];

export default function Landing() {
  usePageMeta({ title: null, description: "Compare universities abroad, see the full 4-year cost in rupees and decide with confidence. AI decision support for Indian and IB families.", path: "/" });
  const nav = useNavigate();
  const { user } = useAuth();
  const [openIndex, setOpenIndex] = useState(0);
  const [cmpIdx, setCmpIdx] = useState(0);
  const [cmpFade, setCmpFade] = useState(true);
  const [bfIdx, setBfIdx] = useState(0);
  const [bfFade, setBfFade] = useState(true);

  /* Rotate compare-table data every 3s with a short fade between swaps */
  useEffect(() => {
    const id = setInterval(() => {
      setCmpFade(false);
      setTimeout(() => {
        setCmpIdx((i) => (i + 1) % COMPARE_SETS.length);
        setCmpFade(true);
      }, 220);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  /* Rotate the Unimora AI recommendation card every 4s with a fade swap */
  useEffect(() => {
    const id = setInterval(() => {
      setBfFade(false);
      setTimeout(() => {
        setBfIdx((i) => (i + 1) % BEST_FIT_SETS.length);
        setBfFade(true);
      }, 240);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const cmp = COMPARE_SETS[cmpIdx];
  const bf = BEST_FIT_SETS[bfIdx];

  const startGoogle = () => {
    startGoogleLogin("/dashboard");
  };

  const cta = () => (user ? nav("/dashboard") : startGoogle());

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <SparkleBanner />
      <Nav />

      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div className="fade-up">
          {/* Brand mark + eyebrow */}
          <div className="flex items-center gap-2.5 mb-6">
            <span className="grad-text" style={{ fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em" }}>
              Unimora AI
            </span>
            <span
              className="text-[var(--s500)] pl-2.5 border-l border-[var(--s200)]"
              style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase" }}
            >
              Decision intelligence
            </span>
          </div>

          {/* H1 */}
          <h1
            className="font-display"
            style={{
              fontWeight: 600,
              letterSpacing: "-0.025em",
              lineHeight: 1.04,
              fontSize: "clamp(40px, 5.2vw, 60px)",
              color: "var(--s900)",
            }}
          >
            From dream to <span className="grad-text">the right university.</span>
          </h1>

          {/* Lead */}
          <p className="mt-6 text-[var(--s900)]" style={{ fontSize: "clamp(17px, 1.6vw, 20px)", fontWeight: 600, lineHeight: 1.4, letterSpacing: "-0.01em", maxWidth: "32ch" }}>
            Unimora AI GUIDE&trade; is an AI-powered engine helping ambitious students and families navigate global university admissions with confidence.
          </p>

          {/* Divider */}
          <div
            className="mt-7 mb-7"
            style={{ width: 56, height: 3, borderRadius: 2, background: "linear-gradient(90deg,#5247E5,#9B5BE8)" }}
            aria-hidden
          />

          {/* Body with highlight */}
          <p className="text-[var(--s600)] leading-relaxed max-w-[46ch]" style={{ fontSize: "16.5px" }}>
            Studying abroad is one of the biggest financial decisions a family will ever make. A{" "}
            <b className="hl-amt">₹40 lakh–₹2 crore</b> investment deserves verified data, transparent financial
            planning, and explainable AI — not guesswork.
          </p>

          {/* Actions */}
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <button
              className="btn-primary-lg cta-glow inline-flex items-center gap-2.5"
              onClick={cta}
              data-testid="hero-start-button"
              style={{
                background: "linear-gradient(110deg,#5247E5,#7A5AF0)",
                boxShadow: "0 12px 24px -10px rgba(82,71,229,.55)",
                borderRadius: 13,
                padding: "15px 26px",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {user ? "Open dashboard" : "Start free"} <ArrowRight size={18} strokeWidth={2.2} />
            </button>
            <a href="#how" className="btn-link text-[var(--ind)]" data-testid="hero-how-link" style={{ fontSize: "15.5px", fontWeight: 600 }}>
              How this works
            </a>
          </div>

          {/* Trust strip */}
          {!user && (
            <div
              className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[var(--s500)]"
              style={{ fontSize: "11.5px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              <span className="inline-flex items-center gap-1.5">
                <Check size={15} strokeWidth={2.4} className="text-[var(--success)]" />
                Sign in securely with Google
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check size={15} strokeWidth={2.4} className="text-[var(--success)]" />
                Free to explore
              </span>
            </div>
          )}
        </div>

        {/* RIGHT — fanning product preview stack */}
        <div className="hero-stack" data-testid="hero-stack">
          {/* CARD 1 — Unimora AI recommendation (auto-rotates every 4s) */}
          <div className="hero-card bf" data-testid="hero-bestfit-card">
            <div className={`bf-swap ${bfFade ? "is-in" : "is-out"}`} key={bfIdx}>
              <div className="bf-top">
                <div>
                  <span className="eyebrow-sm">Unimora AI recommendation</span>
                  <div className="bf-uni">
                    <div className="bf-shield"><ShieldCheck size={20} strokeWidth={1.9} /></div>
                    <div>
                      <div className="t">{bf.name}</div>
                      <div className="c">{bf.tagline}</div>
                    </div>
                  </div>
                </div>
                <div className="reco-badge">
                  <div className="reco-title">Highly recommended</div>
                  <div className="reco-stars">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} strokeWidth={1.5} fill="currentColor" />
                    ))}
                  </div>
                  <div className="reco-score">Decision score · <b className="num">{bf.score}<span>/100</span></b></div>
                </div>
              </div>

              <div className="why">
                {bf.why.map(([head, tail], i) => (
                  <div className="why-row" key={i}>
                    <Check size={17} strokeWidth={2.4} />
                    <span><b>{head}</b> — {tail}</span>
                  </div>
                ))}
              </div>

              <div className="bf-stats">
                {bf.stats.map((s) => (
                  <div key={s.k}>
                    <div className="k">{s.k}</div>
                    <div className="v num">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CARD 2 — Compare table (auto-rotates every 3s) */}
          <div className="hero-card cmp" data-testid="hero-compare-card">
            <div className="flex items-center justify-between">
              <span className="eyebrow-sm">Compare side by side</span>
              <div className="cmp-dots" aria-hidden>
                {COMPARE_SETS.map((_, i) => (
                  <span key={i} className={`cmp-dot ${i === cmpIdx ? "is-active" : ""}`} />
                ))}
              </div>
            </div>
            <div className={`cmp-swap ${cmpFade ? "is-in" : "is-out"}`} key={cmpIdx}>
              <table className="cmp-table">
                <thead>
                  <tr>
                    <th></th>
                    {cmp.cols.map((c) => (
                      <th key={c.name}>
                        <span className="uni">
                          <span className="pip" style={{ background: c.color }} />
                          {c.name}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cmp.rows.map((r) => (
                    <tr key={r.label}>
                      <td>{r.label}</td>
                      {r.vals.map((v, i) => (
                        <td key={i}>
                          <span className={i === r.bestIdx ? "v num" : "num"}>{v}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td>Overall fit</td>
                    {cmp.stars.map((n, i) => (
                      <td key={i}>
                        <span className="stars" style={{ color: cmp.cols[i].color }}>
                          {Array.from({ length: n }).map((_, k) => (
                            <Star key={k} size={13} strokeWidth={1.5} fill="currentColor" />
                          ))}
                        </span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* CARD 3 — Cost chart */}
          <div className="hero-card cost">
            <div className="chart-head">
              <span className="eyebrow-sm">Total 4-year cost (INR)</span>
              <div className="rupee">₹</div>
            </div>
            <svg width="100%" height="120" viewBox="0 0 320 120" preserveAspectRatio="none" style={{ marginTop: 12, display: "block" }}>
              <defs>
                <linearGradient id="hero-cost-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="rgba(82,71,229,.28)" />
                  <stop offset="1" stopColor="rgba(82,71,229,0)" />
                </linearGradient>
              </defs>
              <path d="M16,96 L104,80 L192,56 L300,34 L300,116 L16,116 Z" fill="url(#hero-cost-area)" />
              <path d="M16,96 L104,80 L192,56 L300,34" fill="none" stroke="#5247E5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16,104 L104,94 L192,80 L300,66" fill="none" stroke="#7A5AF0" strokeWidth="2" strokeDasharray="3 3" opacity=".7" />
              <circle cx="300" cy="34" r="3.5" fill="#5247E5" />
            </svg>
            <div className="xlabels">
              <span>Year 1</span><span>Year 2</span><span>Year 3</span><span>Year 4</span>
            </div>
            <div className="legend">
              <span><i style={{ background: "#5247E5" }} />Tuition</span>
              <span><i style={{ background: "#7A5AF0" }} />Living</span>
              <span><i style={{ background: "#C9C5F4" }} />Other</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST METRICS — tall white cards (LABEL · METRIC · SUPPORT hierarchy) */}
      <section className="bg-[var(--bg)] border-y border-[var(--s200)]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
            {[
              { label: "Average investment", metric: "₹40L – ₹2Cr", support: "Typical family range" },
              { label: "Universities",       metric: "100+",        support: "In our verified database" },
              { label: "Sources cited",      metric: "100%",        support: "Every figure linked" },
              { label: "Scholarships",       metric: "Tailored",    support: "Matched to your profile" },
              { label: "Countries",          metric: "11",          support: "English-speaking · EU · Asia" },
            ].map((m) => (
              <div
                key={m.label}
                className="group bg-white border border-[var(--s200)] rounded-[var(--r20)] px-5 py-5 md:px-6 md:py-6 transition-all hover:-translate-y-1 hover:border-[var(--s300)] hover:shadow-lg"
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#94A3B8",
                  }}
                >
                  {m.label}
                </div>
                <div
                  className="mt-2"
                  style={{
                    fontSize: "clamp(20px, 5.6vw, 32px)",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "#4F46E5",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.1,
                    wordBreak: "keep-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {m.metric}
                </div>
                <div
                  className="mt-2"
                  style={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#64748B",
                    lineHeight: 1.45,
                  }}
                >
                  {m.support}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM → SOLUTION (2 halves) */}
      <section>
        {/* Half 1 — white, "The Problem" */}
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)]" />
            <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">The Problem</span>
          </div>
          <h2 className="font-display font-semibold text-[var(--s900)] text-h2 md:text-h1 lg:text-display leading-tight whitespace-nowrap">
            Choosing a university shouldn&apos;t depend on
          </h2>
          <div className="mt-8 flex flex-nowrap items-center gap-2.5 md:gap-3 overflow-x-auto md:overflow-visible">
            {[
              "Rankings",
              "WhatsApp advice",
              "Random YouTube videos",
              "Expensive consultants",
              "Guesswork",
            ].map((t) => (
              <div
                key={t}
                className="shrink-0 inline-flex items-center gap-2 bg-white border border-[var(--s200)] rounded-[var(--r20)] px-4 py-2.5 shadow-sm whitespace-nowrap"
              >
                <span className="text-[var(--danger)] text-body font-semibold leading-none">✕</span>
                <span className="text-body-sm font-medium text-[var(--s800)]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Half 2 — indigo, "Instead" */}
      <section className="relative overflow-hidden bg-[var(--ind)] text-white">
        {/* subtle grid pattern overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            <span className="text-micro font-semibold tracking-[0.05em] uppercase text-white/85">Instead</span>
          </div>
          <h2 className="font-display font-semibold text-white text-h2 md:text-h1 lg:text-display leading-tight whitespace-nowrap">
            Make every decision using verified data.
          </h2>
          <div className="mt-8 flex flex-nowrap items-center gap-2.5 md:gap-3 overflow-x-auto md:overflow-visible">
            {[
              "Tuition Costs",
              "Scholarships",
              "Careers",
              "Visa Success",
              "Employability",
              "AI Reasoning",
            ].map((t) => (
              <div
                key={t}
                className="shrink-0 inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/25 rounded-[var(--r20)] px-4 py-2.5 transition-colors hover:bg-white/10 whitespace-nowrap"
              >
                <span className="text-[var(--success)] text-body-sm font-semibold leading-none">✓</span>
                <span className="text-body-sm font-medium text-white">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW UNIMORA AI WORKS — 5-step horizontal timeline */}
      <section id="how" className="bg-white border-y border-[var(--s200)]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)]" />
            <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">How Unimora AI Works</span>
          </div>
          <h2 className="font-display font-semibold text-[var(--s900)] text-h1 md:text-display lg:text-display leading-tight max-w-4xl">
            Five steps from confusion to confidence.
          </h2>

          <div className="relative mt-20">
            {/* Dashed connector line — desktop only, sits behind circles */}
            <div
              className="hidden md:block absolute left-[10%] right-[10%] border-t-2 border-dashed border-[var(--s200)]"
              style={{ top: "40px" }}
              aria-hidden
            />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-6 relative">
              {[
                {
                  icon: UserRound,
                  title: "Create Profile",
                  desc: "Tell us your goals, academics — IB profile or IGCSE score, target countries, budget and intended course.",
                  bg: "#EEF2FF", fg: "#4F46E5", ring: "rgba(79,70,229,0.18)",  // indigo
                },
                {
                  icon: Columns3,
                  title: "Compare Universities",
                  desc: "Place up to four universities on the same table. Cost, ranking, employment, visa, trade-offs and outcomes.",
                  bg: "#ECFDF5", fg: "#059669", ring: "rgba(5,150,105,0.18)",  // mint
                },
                {
                  icon: LineChart,
                  title: "Financial Planning",
                  desc: "Estimate total investment, loans and scholarships with a 3-to-4 year cost projection — tuition, living, one-time costs, inflation. Every assumption visible.",
                  bg: "#F5F3FF", fg: "#7C3AED", ring: "rgba(124,58,237,0.18)", // violet
                },
                {
                  icon: MessageCircle,
                  title: "AI Counsel",
                  desc: "An AI advisor trained never to fabricate. It labels estimates, surfaces trade-offs, and refuses to make the decision for you.",
                  bg: "#FFFBEB", fg: "#D97706", ring: "rgba(217,119,6,0.20)",  // amber
                },
                {
                  icon: CheckCheck,
                  title: "Apply Confidently",
                  desc: "Receive evidence-backed recommendations to take confident decisions.",
                  bg: "#FDF4FF", fg: "#C026D3", ring: "rgba(192,38,211,0.18)", // fuchsia
                },
              ].map((s, idx) => (
                <div
                  key={s.title}
                  className="how-step flex flex-col items-center text-center fade-up"
                  style={{ animationDelay: `${idx * 110}ms` }}
                  data-testid={`how-step-${idx + 1}`}
                >
                  <div
                    className="how-circle relative z-10 w-20 h-20 rounded-full flex items-center justify-center"
                    style={{
                      background: s.bg,
                      color: s.fg,
                      boxShadow: `0 0 0 8px #fff, 0 0 0 9px ${s.ring}`,
                      "--how-ring-color": s.ring,
                    }}
                  >
                    <s.icon size={28} strokeWidth={1.7} />
                    <span
                      className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full text-micro font-semibold flex items-center justify-center bg-white shadow-sm"
                      style={{ color: s.fg, border: `1.5px solid ${s.fg}` }}
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                  </div>
                  <div className="mt-7 font-display text-h3 font-semibold text-[var(--s900)]">{s.title}</div>
                  <p className="mt-3 text-body-sm text-[var(--s600)] leading-relaxed max-w-[220px]">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT SHOWCASE — One platform, every decision made visible */}
      <section className="bg-white border-t border-[var(--s200)]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
          <h2 className="font-display font-semibold text-[var(--s900)] text-h2 md:text-h1 lg:text-display leading-tight mb-20 whitespace-nowrap">
            One platform, every decision made visible.
          </h2>

          {/* Row 1 — University Comparison */}
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center mb-32">
            <div>
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)]" />
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">University Comparison</span>
              </div>
              <h3 className="font-display font-semibold text-[var(--s900)] text-h1 md:text-h1 leading-tight">
                See every university side by side.
              </h3>
              <p className="text-body leading-relaxed text-[var(--s600)] mt-5 max-w-md">
                Compare tuition, living costs, scholarships, career ROI, visa outlook, employability and safety in one verified view — no more juggling ten browser tabs.
              </p>
              <div className="mt-7 space-y-3">
                {["Live AI match score for each option", "Student satisfaction & safety ratings"].map(b => (
                  <div key={b} className="flex items-center gap-2.5 text-body-sm text-[var(--s800)]">
                    <CheckCheck size={16} strokeWidth={2} className="text-[var(--ind)]" /> {b}
                  </div>
                ))}
              </div>
            </div>
            <BrowserMockup>
              <table className="w-full text-label">
                <thead>
                  <tr className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">
                    <th className="text-left py-3 pr-2 font-semibold">University</th>
                    <th className="text-left py-3 px-2">Tuition</th>
                    <th className="text-left py-3 px-2">Living</th>
                    <th className="text-left py-3 px-2">Scholar.</th>
                    <th className="text-left py-3 px-2">ROI</th>
                    <th className="text-left py-3 pl-2">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["U. Melbourne","₹32L","₹9.2L","₹18.4L","4.2x","96%",true],
                    ["U. Toronto","₹28L","₹11L","₹9L","3.6x","88%"],
                    ["TU Munich","₹2.1L","₹7.8L","₹4L","4.8x","84%"],
                    ["U. Manchester","₹24L","₹8.6L","₹6L","3.2x","79%"],
                  ].map((r, i) => (
                    <tr key={i} className="border-t border-[var(--s200)]">
                      <td className={`py-3.5 pr-2 ${r[6] ? "text-[var(--ind)] font-semibold" : "text-[var(--s800)]"}`}>{r[0]}</td>
                      <td className="py-3.5 px-2 text-[var(--s800)]">{r[1]}</td>
                      <td className="py-3.5 px-2 text-[var(--s800)]">{r[2]}</td>
                      <td className="py-3.5 px-2 text-[var(--s800)]">{r[3]}</td>
                      <td className="py-3.5 px-2 text-[var(--s800)]">{r[4]}</td>
                      <td className="py-3.5 pl-2">
                        <span className="inline-block bg-[var(--ind-soft)] text-[var(--ind)] text-micro font-semibold px-2 py-1 rounded-[var(--r8)]">{r[5]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </BrowserMockup>
          </div>

          {/* Row 2 — Financial Planner (reverse) */}
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center mb-32">
            <BrowserMockup className="md:order-1">
              <div className="text-body-sm">
                {[
                  ["Tuition","₹32.0L",""],
                  ["Living Cost","₹9.2L",""],
                  ["Loan","₹15.0L",""],
                  ["Parent Contribution","₹12.0L",""],
                  ["Savings","₹4.0L",""],
                  ["Scholarship","−₹18.4L","text-[var(--success)]"],
                ].map(([k,v,c], i, a) => (
                  <div key={k} className={`flex justify-between items-center py-3.5 ${i < a.length - 1 ? "border-b border-[var(--s100)]" : ""}`}>
                    <span className="text-[var(--s700)]">{k}</span>
                    <span className={`font-semibold ${c || "text-[var(--s900)]"}`}>{v}</span>
                  </div>
                ))}
                <div className="mt-5 mb-2 grid grid-cols-12 gap-1 h-2">
                  <div className="col-span-5 rounded-full bg-[var(--ind)]" />
                  <div className="col-span-3 rounded-full bg-indigo-300" />
                  <div className="col-span-2 rounded-full bg-indigo-200" />
                  <div className="col-span-2 rounded-full bg-indigo-100" />
                </div>
                <div className="mt-5 pt-4 border-t border-[var(--s200)] flex justify-between items-baseline">
                  <span className="font-semibold text-[var(--s900)]">Total Investment</span>
                  <span className="text-[var(--s800)]"><span className="font-display text-h3 font-semibold text-[var(--s900)] mr-2">₹22.6L</span><span className="text-label text-[var(--s500)]">· Break-even in 3.1 yrs</span></span>
                </div>
              </div>
            </BrowserMockup>
            <div className="md:order-2">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)]" />
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">Financial Planner</span>
              </div>
              <h3 className="font-display font-semibold text-[var(--s900)] text-h1 md:text-h1 leading-tight">
                Know the real number, today.
              </h3>
              <p className="text-body leading-relaxed text-[var(--s600)] mt-5 max-w-md">
                Unimora AI breaks down tuition, living cost, loan, parent contribution, savings and scholarship into a single transparent total — plus your break-even period.
              </p>
              <div className="mt-7 space-y-3">
                {["Editable loan & contribution scenarios", "Break-even period, calculated instantly"].map(b => (
                  <div key={b} className="flex items-center gap-2.5 text-body-sm text-[var(--s800)]">
                    <CheckCheck size={16} strokeWidth={2} className="text-[var(--ind)]" /> {b}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3 — AI Recommendation */}
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center mb-32">
            <div>
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)]" />
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">AI Recommendation</span>
              </div>
              <h3 className="font-display font-semibold text-[var(--s900)] text-h1 md:text-h1 leading-tight">
                Recommendations that explain themselves.
              </h3>
              <p className="text-body leading-relaxed text-[var(--s600)] mt-5 max-w-md">
                No black-box scores. Every suggestion comes with the underlying reasoning, weighted by what matters most to you.
              </p>
              <div className="mt-7 space-y-3">
                <div className="flex items-center gap-2.5 text-body-sm text-[var(--s800)]">
                  <CheckCheck size={16} strokeWidth={2} className="text-[var(--ind)]" /> Confidence and explainability, side by side
                </div>
              </div>
            </div>
            <BrowserMockup>
              <div className="rounded-[var(--r12)] bg-[var(--ind-soft)] p-5 text-body-sm leading-relaxed text-[var(--s800)]">
                Based on your goals, <span className="text-[var(--ind)] font-semibold">Melbourne ranks first</span> because it offers the highest career ROI within your budget, a strong scholarship match, and consistently high visa approval rates for your course.
              </div>
              <div className="grid grid-cols-2 gap-6 mt-6 pt-4 border-t border-[var(--s200)]">
                <div>
                  <div className="text-label text-[var(--s500)] mb-1">Confidence</div>
                  <div className="font-display text-h1 font-semibold text-[var(--ind)] leading-none">94%</div>
                </div>
                <div>
                  <div className="text-label text-[var(--s500)] mb-1">Reasoning</div>
                  <div className="font-display text-h3 font-semibold text-[var(--ind)] leading-snug">Every pick shows its sources</div>
                </div>
              </div>
            </BrowserMockup>
          </div>

          {/* Row 4 — Decision Dashboard (reverse) */}
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            <BrowserMockup className="md:order-1">
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="rounded-[var(--r12)] border border-[var(--s200)] p-4">
                  <div className="text-label text-[var(--s500)]">Total Investment</div>
                  <div className="font-display text-h2 font-semibold text-[var(--s900)] mt-1">₹22.6L</div>
                </div>
                <div className="rounded-[var(--r12)] border border-[var(--s200)] p-4">
                  <div className="text-label text-[var(--s500)]">Employability</div>
                  <div className="font-display text-h2 font-semibold text-[var(--s900)] mt-1">92%</div>
                </div>
              </div>
              <div className="flex justify-center mt-2">
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 36 36" className="w-32 h-32 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--s200)" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--ind)" strokeWidth="3" strokeDasharray="62 100" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-body-sm font-semibold text-[var(--s900)]">62% Funded</div>
                </div>
              </div>
            </BrowserMockup>
            <div className="md:order-2">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)]" />
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">Decision Dashboard</span>
              </div>
              <h3 className="font-display font-semibold text-[var(--s900)] text-h1 md:text-h1 leading-tight">
                Your entire decision, at a glance.
              </h3>
              <p className="text-body leading-relaxed text-[var(--s600)] mt-5 max-w-md">
                KPI cards and charts bring every factor — cost, outcome, risk — into one clear view your whole family can understand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DECISION INTELLIGENCE — Your AI Education Advisor (indigo) */}
      <section className="relative overflow-hidden bg-[var(--ind)] text-white">
        {/* glow orbs */}
        <div className="absolute -top-20 -right-32 w-[480px] h-[480px] rounded-full bg-indigo-400/30 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-indigo-300/20 blur-3xl pointer-events-none" aria-hidden />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            <span className="text-micro font-semibold tracking-[0.05em] uppercase text-white/85">Decision Intelligence</span>
          </div>
          <h2 className="font-display font-semibold text-white text-display md:text-display leading-none max-w-3xl">
            Your AI Education Advisor
          </h2>
          <p className="text-body-lg leading-relaxed text-white/75 mt-5 max-w-lg">
            Ask anything about cost, ROI or visa outcomes — get an evidence-backed answer in seconds.
          </p>

          {/* Chat glass card */}
          <div className="mt-10 rounded-[24px] bg-white/5 backdrop-blur-xl border border-white/15 p-5 md:p-7 max-w-5xl shadow-2xl">
            {/* Suggested chips — single row */}
            <div className="flex flex-nowrap items-center gap-2.5 overflow-x-auto">
              {[
                { t: "Should I choose Germany?", active: true },
                { t: "Can I afford Melbourne?" },
                { t: "Is Sydney worth ₹70L?" },
                { t: "Which university has the best ROI?" },
              ].map((c) => (
                <div
                  key={c.t}
                  className={`shrink-0 whitespace-nowrap text-body-sm font-medium px-4 py-2 rounded-full border ${
                    c.active
                      ? "bg-white text-[var(--ind)] border-white shadow-md"
                      : "bg-white/5 text-white/85 border-white/20"
                  }`}
                >
                  {c.t}
                </div>
              ))}
            </div>

            {/* Answer card */}
            <div className="mt-5 rounded-[var(--r20)] bg-white/8 border border-white/15 p-5 md:p-7">
              <p className="text-body leading-relaxed text-white/90">
                Germany offers <span className="font-semibold text-white">near-zero tuition</span> at public universities, making it a strong fit for your budget. Career ROI is high (4.6× average) though English-taught programs are competitive — your profile shows an 84% match.
              </p>
              <div className="grid grid-cols-2 gap-8 mt-5 pt-5 border-t border-white/15">
                <div>
                  <div className="text-micro font-semibold tracking-[0.05em] uppercase text-white/55 mb-2">Confidence</div>
                  <div className="font-display text-h1 font-semibold text-white leading-none">88%</div>
                  <div className="mt-2.5 h-1.5 rounded-full bg-white/15 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-white/60 to-white/90" style={{ width: "88%" }} />
                  </div>
                </div>
                <div>
                  <div className="text-micro font-semibold tracking-[0.05em] uppercase text-white/55 mb-2">Reasoning</div>
                  <div className="font-display text-h3 font-semibold text-white leading-tight">Sources on every claim</div>
                  <div className="mt-2.5 text-label text-white/65 leading-snug">Hover any number to see where it came from.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES — collapsible accordion */}
      <section className="bg-[var(--bg)] border-y border-[var(--s200)]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow mb-3">Guiding principles</div>
          <h2 className="font-display font-semibold text-[var(--s900)] text-h1 md:text-display leading-tight mb-8">
            Eight rules we will not bend.
          </h2>
          <div className="space-y-2 max-w-3xl">
            {PRINCIPLES.map((p, idx) => (
              <div key={p.head} className="border-b border-[var(--s200)]">
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                  data-testid={`principle-${idx}`}
                  className="w-full flex items-center justify-between py-5 text-left"
                >
                  <span className="font-display text-h3 font-semibold text-[var(--s900)]">{p.head}</span>
                  <ChevronDown size={20} strokeWidth={1.8} className={`text-[var(--s400)] transition-transform ${openIndex === idx ? "rotate-180" : ""}`} />
                </button>
                {openIndex === idx && (
                  <div className="pb-6 text-body leading-relaxed text-[var(--s600)] space-y-2">
                    {p.body.map(([k, v]) => (
                      <p key={k}><strong className="text-[var(--s900)] font-semibold">{k}</strong> — {v}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP — indigo */}
      <section className="relative overflow-hidden bg-[var(--ind)] text-white">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-indigo-400/30 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-indigo-300/20 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <h2 className="font-display font-semibold text-h2 md:text-h1 lg:text-h1 leading-tight tracking-tight max-w-3xl">
            A clearer path to the right university for your child&apos;s future.<br />
            Ready to make the biggest decision of your future?
          </h2>
          <p className="mt-6 text-white/80 text-body">
            <span className="inline-flex items-center gap-1.5"><span className="text-[var(--success)] font-semibold">✓</span> Sign in securely with Google</span>
            <span className="mx-3 text-white/30">·</span>
            <span className="inline-flex items-center gap-1.5"><span className="text-[var(--success)] font-semibold">✓</span> Free to explore</span>
          </p>
          <div className="mt-8">
            <button
              onClick={cta}
              data-testid="cta-start-button"
              className="inline-flex items-center gap-2 bg-white text-[var(--ind)] font-semibold text-body-lg px-8 py-3.5 rounded-[var(--r12)] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Start free <ArrowRight size={16} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function BrowserMockup({ children, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-indigo-100 via-white to-indigo-50 blur-xl opacity-60 pointer-events-none" aria-hidden />
      <div className="relative bg-white rounded-[var(--r20)] border border-[var(--s200)] shadow-xl overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[var(--s100)] bg-[var(--bg)]">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--s200)]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--s200)]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--s200)]" />
        </div>
        <div className="p-6 md:p-7">{children}</div>
      </div>
    </div>
  );
}
